import { useRef, useState } from "react";
import { toast } from "react-toastify";
import { uploadMenuThumbnails } from "../api";
import { MEDIA_TYPE } from "../useMenuThumbnails";

export const TYPE_OPTIONS = [
  { id: MEDIA_TYPE.podcast, label: "Podcast" },
  { id: MEDIA_TYPE.video, label: "Video" },
  { id: MEDIA_TYPE.shorts, label: "Shorts" },
  { id: MEDIA_TYPE.news, label: "News" },
  { id: MEDIA_TYPE.program, label: "Program" },
];

const ALLOWED_EXTENSIONS = ["jpg", "jpeg", "png", "webp"];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // matches the API's max:5120 (KB)
const MAX_FILES = 20;

const getExtension = (name) => name.slice(name.lastIndexOf(".") + 1).toLowerCase();

const getTitle = (name) => {
  const dot = name.lastIndexOf(".");
  return (dot > 0 ? name.slice(0, dot) : name).trim();
};

// used for the preview and to show the image's ratio before uploading
const readImage = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () =>
        resolve({ preview: reader.result, width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => resolve({ preview: reader.result, width: 0, height: 0 });
      img.src = reader.result;
    };
    reader.onerror = () => resolve({ preview: "", width: 0, height: 0 });
    reader.readAsDataURL(file);
  });

const useBulkUpload = ({ refetch, handleClose }) => {
  const fileInputRef = useRef(null);
  const [type, setType] = useState(null);
  const [typeError, setTypeError] = useState("");
  const [items, setItems] = useState([]);
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const resetForm = () => {
    setItems([]);
    setType(null);
    setTypeError("");
    setProgress(0);
    setIsUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleTypeChange = (option) => {
    setType(option);
    setTypeError("");
  };

  const handleFileChange = async (e) => {
    const selected = Array.from(e.target.files ?? []);
    e.target.value = "";
    const rejected = [];
    const accepted = [];
    selected.forEach((file, index) => {
      if (!ALLOWED_EXTENSIONS.includes(getExtension(file.name)) || file.size > MAX_FILE_SIZE) {
        rejected.push(file.name);
        return;
      }
      // ignore images already in the list
      if (items.some((item) => item.name === file.name && item.size === file.size)) return;
      accepted.push({
        id: `${Date.now()}-${index}-${file.name}`,
        file,
        name: file.name,
        size: file.size,
        title: getTitle(file.name),
      });
    });
    if (rejected.length > 0) {
      toast.error(
        `Skipped ${rejected.length} file(s). Only JPG, JPEG, PNG or WEBP up to 5 MB are allowed.`
      );
    }
    const withPreviews = await Promise.all(
      accepted.map(async (item) => ({ ...item, ...(await readImage(item.file)) }))
    );
    setItems((prev) => {
      const next = [...prev, ...withPreviews];
      if (next.length > MAX_FILES) {
        toast.error(`A maximum of ${MAX_FILES} images can be uploaded at a time.`);
        return next.slice(0, MAX_FILES);
      }
      return next;
    });
  };

  const handleRemove = (id) => setItems((prev) => prev.filter((item) => item.id !== id));

  const handleUpload = async () => {
    if (!type) {
      setTypeError("*Select a type");
      return;
    }
    if (items.length === 0) {
      toast.error("Select at least one image");
      return;
    }
    setIsUploading(true);
    setProgress(0);

    const formData = new FormData();
    formData.append("type", type.id);
    items.forEach((item) => formData.append("files[]", item.file));

    const response = await uploadMenuThumbnails(formData, (event) => {
      if (event.total) setProgress(Math.round((event.loaded * 100) / event.total));
    });
    setIsUploading(false);

    if (response?.status === 200 && response?.data?.success) {
      const uploaded = response?.data?.data?.uploaded_files ?? items.length;
      const failed = response?.data?.data?.failed_files ?? [];
      toast.success(`${uploaded} ${type.label.toLowerCase()} thumbnail(s) uploaded`);
      if (failed.length > 0) {
        toast.error(`${failed.length} file(s) could not be uploaded: ${failed.join(", ")}`);
      }
      resetForm();
      handleClose();
      refetch?.();
    } else {
      const errors = response?.errors ?? response?.data?.errors;
      const message =
        (errors && Object.values(errors).flat()[0]) ??
        response?.message ??
        response?.data?.message ??
        "Something went wrong";
      toast.error(message);
    }
  };

  const handleCancel = () => {
    resetForm();
    handleClose();
  };

  return {
    fileInputRef,
    type,
    typeError,
    items,
    progress,
    isUploading,
    acceptTypes: ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(","),
    handleTypeChange,
    handleFileChange,
    handleRemove,
    handleUpload,
    handleCancel,
  };
};

export default useBulkUpload;
